/**
 * Module Registry - Note'n Module System
 *
 * Defines all available application modules with metadata.
 * Single source of truth for module definitions (labels, icons, required status).
 *
 * @module core/config/modules
 */

import type { AppModule } from '~/core/store/useGlobalStore';
import { QUESTIONS_ENABLED, INK_ENABLED } from '~/core/config/featureFlags';

// ============================================================================
// TYPES
// ============================================================================

export type ModuleSection = 'ecosystem' | 'teaching';

export interface ModuleDefinition {
  /** Unique module identifier */
  id: AppModule;
  /** Display label (Korean) */
  label: string;
  /** Short description */
  description: string;
  /** Lucide icon name (resolved at component level) */
  iconName: string;
  /** Whether this module cannot be unpinned */
  isRequired: boolean;
  /** Default sort order */
  defaultOrder: number;
  /** Section grouping for Dock display */
  section: ModuleSection;
}

// ============================================================================
// MODULE REGISTRY
// ============================================================================

export const ALL_MODULES: ModuleDefinition[] = [
  {
    id: 'DISPATCHER',
    label: '타임라인',
    description: '일별 수업 타임라인 및 스케줄 관리',
    iconName: 'Clock',
    isRequired: true,
    defaultOrder: 0,
    section: 'teaching',
  },
  {
    id: 'CALENDAR',
    label: '캘린더',
    description: '월간 캘린더 뷰 및 일정 관리',
    iconName: 'CalendarDays',
    isRequired: false,
    defaultOrder: 1,
    section: 'teaching',
  },
  {
    id: 'SESSIONS',
    label: '세션',
    description: '수업 세션 등록 및 관리',
    iconName: 'Layers',
    isRequired: false,
    defaultOrder: 2,
    section: 'teaching',
  },
  {
    id: 'STUDENT_MANAGE',
    label: '학생',
    description: '학생 정보 관리 및 현황',
    iconName: 'GraduationCap',
    isRequired: false,
    defaultOrder: 3,
    section: 'teaching',
  },
  {
    id: 'SCHOOLS',
    label: '학교',
    description: '학교별 학생·시험 관리',
    iconName: 'School',
    isRequired: false,
    defaultOrder: 4,
    section: 'teaching',
  },
  {
    id: 'FLOWS',
    label: '흐름',
    description: '반(코스) 편성 및 커리큘럼 관리',
    iconName: 'Flows',
    isRequired: false,
    defaultOrder: 5,
    section: 'teaching',
  },
  {
    id: 'MESSAGES',
    label: '메시지',
    description: '학부모/학생 메시지 및 알림',
    iconName: 'MessageSquare',
    isRequired: false,
    defaultOrder: 6,
    section: 'teaching',
  },
  {
    id: 'SCORES',
    label: '성적',
    description: '시험 생성 및 점수 입력',
    iconName: 'ClipboardList',
    isRequired: false,
    defaultOrder: 7,
    section: 'teaching',
  },
  {
    id: 'TEXTBOOKS',
    label: '교재',
    description: '교재 및 단원 관리',
    iconName: 'BookText',
    isRequired: false,
    defaultOrder: 8,
    section: 'teaching',
  },
  {
    id: 'STATS',
    label: '통계',
    description: '주간 수업 현황 대시보드',
    iconName: 'BarChart2',
    isRequired: false,
    defaultOrder: 9,
    section: 'teaching',
  },
  {
    id: 'NOTEBOOKS',
    label: '메모장',
    description: '자유형 마크다운 메모장',
    iconName: 'NotebookPen',
    isRequired: false,
    defaultOrder: 10,
    section: 'teaching',
  },
  {
    id: 'QUESTIONS',
    label: '문제',
    description: '문제은행 관리 및 시험지 구성',
    iconName: 'FileQuestion',
    isRequired: false,
    defaultOrder: 11,
    section: 'ecosystem',
  },
  {
    id: 'INK',
    label: '잉크',
    description: 'AI 어시스턴트와 대화',
    iconName: 'Sparkles',
    isRequired: false,
    defaultOrder: 12,
    section: 'ecosystem',
  },
];

// ============================================================================
// DERIVED CONSTANTS
// ============================================================================

/**
 * 출시 게이팅이 적용된 "현재 노출 모듈" 집합.
 * QUESTIONS(문제집)는 트랙2 분리 중이라 출시 빌드에서 숨김 (`featureFlags.QUESTIONS_ENABLED`).
 * INK(잉크 AI)는 서버 에이전트는 작동하나 클라 Sprint 1 미완이라 숨김 (`featureFlags.INK_ENABLED`).
 *   → 둘 다 dev 노출 / prod 숨김. 충분히 개발·테스트 후 공개.
 * 핀/검증/섹션 파생상수는 모두 이 집합에서 도출 → 단일 게이트로 ModulePad·Dock·
 * 설정 동기화·딥링크 폴백이 일관되게 차단된다.
 * `ALL_MODULES`/`getModuleDefinition` 은 불변 — 방어적 lookup(스테일 currentModule 등)이
 * throw 하지 않도록 정의 자체는 보존한다.
 */
const VISIBLE_MODULES: ModuleDefinition[] = ALL_MODULES.filter(
  (m) =>
    (QUESTIONS_ENABLED || m.id !== 'QUESTIONS') &&
    (INK_ENABLED || m.id !== 'INK'),
);

/** Module IDs that cannot be unpinned */
export const REQUIRED_MODULES: AppModule[] = ALL_MODULES
  .filter((m) => m.isRequired)
  .map((m) => m.id);

/** Default pinned modules (노출 모듈 전체, in default order) */
export const DEFAULT_PINNED_MODULES: AppModule[] = VISIBLE_MODULES.map((m) => m.id);

/** All valid module IDs (게이팅 반영) */
export const VALID_MODULE_IDS: AppModule[] = VISIBLE_MODULES.map((m) => m.id);

/** Ecosystem section modules (문제집은 게이팅 시 제외, INK 등) */
export const ECOSYSTEM_MODULES = VISIBLE_MODULES.filter((m) => m.section === 'ecosystem');

/** Teaching section modules (수업 관리) */
export const TEACHING_MODULES = VISIBLE_MODULES.filter((m) => m.section === 'teaching');

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get module definition by ID
 */
export function getModuleDefinition(id: AppModule): ModuleDefinition {
  const mod = ALL_MODULES.find((m) => m.id === id);
  if (!mod) throw new Error(`Unknown module: ${id}`);
  return mod;
}
